import React, { useContext } from "react";
import styled from "styled-components";
import BqnContext from "common/BqnContext";
import ActionsContext from "common/ActionsContext";
import LinkStateHelpModal from "./Help";
import DetailModal from "./Detail";
import CaptureModal from "./Capture";
import HandleEnableOrDisable from "./HandleEnableOrDisable";
import { parseInterfaces } from "./api";
import View from "common/View";
import Table from "common/graphs/Table/Table";
import { atoiv6 } from "common/utils";
import { TickOrCross } from "common/graphs/Table/TableRenders";
import withReloading from "common/withReloading";

const TableContainer = styled.div`
  td i.material-icons {
    font-size: 18px;
  }
  button[title="Enable"] {
    i.material-icons {
      color: var(--signal-green) !important;
    }
  }
  button[title="Disable"] {
    i.material-icons {
      color: var(--signal-red) !important;
    }
  }
`;

const doesNothing = () => {};

const Management = ({ is = false }) =>
  is !== true ? null : <i className="material-icons">radio_button_checked</i>;

const getColumns = (mode, onActionClick) => {
  return [
    ...(mode !== "standalone"
      ? [
          {
            label: "BQN",
            id: "bqn",
            numeric: true,
          },
        ]
      : []),
    {
      label: "NAME",
      id: "name",
      numeric: false,
      cellClassName: (value, row) =>
        row["type"] === "n/a" ? null : "hyperlink-text",
      onClick: (value, row) => {
        if (row["type"] === "n/a") {
          return;
        }
        return onActionClick("details", { name: value, type: row["type"] });
      },
    },
    {
      label: "TYPE",
      id: "type",
      numeric: false,
    },
    {
      label: "MAC",
      id: "mac",
      numeric: false,
      parse: (value) => !value ? 0 : atoiv6(value),
      parseFilter: (value) => value
    },
    {
      label: "UP",
      id: "enabled",
      numeric: false,
      renderCell: (value) => <TickOrCross ok={value} />,
      parse: (value) => value ? "yes" : "no"
    },
    {
      label: "LINK",
      id: "connected",
      numeric: false,
      renderCell: (value) => <TickOrCross ok={value} />,
      parse: (value) => value ? "yes" : "no"
    },
    {
      label: "MGT",
      id: "management",
      numeric: false,
      renderCell: (value) => <Management is={value} />,
      parse: (value) => value ? "yes" : "no"
    },
    {
      label: "ACTIONS",
      id: "ACTIONS",
      actions: [
        {
          label: "Enable",
          icon: "arrow_upward",
          onClick: (row) => {
            if (row["enabled"] === true) {
              return;
            }
            onActionClick("update", { name: row["name"], enable: true });
          },
          disabled: (row) => row["enabled"] === true || row["type"] === "n/a",
          hide: (row) =>
            row["type"] === "loopback" ||
            row["type"] === "n/a" ||
            row["management"] === true,
        },
        {
          label: "Disable",
          icon: "arrow_downward",
          onClick: (row) => {
            if (row["enabled"] === false) {
              return;
            }
            onActionClick("update", { name: row["name"], disable: true });
          },
          disabled: (row) => row["enabled"] !== true || row["type"] === "n/a",
          hide: (row) =>
            row["type"] === "loopback" ||
            row["type"] === "n/a" ||
            row["management"] === true,
        },
        {
          label: "Capture",
          icon: "search",
          onClick: (row) => {
            if (row["enabled"] === false) {
              return;
            }
            onActionClick("capture", {
              name: row["name"],
              type: row["type"],
              bqnId: row["bqn"],
            });
          },
          hide: (row) => row["type"] === "loopback" || row["type"] === "n/a",
        },
      ],
    },
  ];
};

const ListInterfaces = ({ outcome: interfaces = [], onActionClick = doesNothing }) => {
  const {
    clusterStatus: { mode },
  } = useContext(BqnContext);
  const columns = getColumns(mode, onActionClick);
  return (
    <TableContainer>
      <Table
        columns={columns}
        data={interfaces}
        exportCSV={false}
        isRowSelectable={false}
        rowsPerPageNr={50}
      />
    </TableContainer>
  );
};

const filterGivenProps = ({ mode, bqnId }) =>
  (mode === "standalone" || bqnId === "all")
    ? interfaces => interfaces
    : interfaces => interfaces.filter(({ bqn }) => parseFloat(bqn) === parseFloat(bqnId));

const doRequest = props =>
  ifCl
    .run("show interface raw")
    .then(parseInterfaces)
    .then(filterGivenProps(props))

const ListInterfacesWithReloading = withReloading(ListInterfaces);

const LinkState = ({ returnView }) => {
  const {
    clusterStatus: { mode },
    bqnId,
  } = useContext(BqnContext);
  const actions = useContext(ActionsContext);

  const onActionClick = (field, params) => {
    actions.send(`show-interface-${field}`, params);
  };

  return (
    <View returnView={returnView}
      Help={LinkStateHelpModal}
      className="container-lg"
      title="Network Interfaces"
    >
      <div className="body flex-panel-column padding-20 expect-overflow">
        <ListInterfacesWithReloading
          key={bqnId || 'default'}
          provider={() => doRequest({ mode, bqnId })}
          onActionClick={onActionClick}
        />
      </div>
      <CaptureModal />
      <DetailModal />
      <HandleEnableOrDisable />
    </View>
  );
};

export default LinkState;
